import { ref, readonly, computed } from 'vue'

// Shared state at module level (singleton)
const wantsOpen = ref(false)
// Optional invite code context (for school join flows)
const inviteCodeMode = ref(false)
// Open straight on the password form. A teacher whose school gateway eats our
// code emails is sent here from the redeem page's dead end — landing them on
// "we'll send you a code" would be sending them back at the wall they just hit.
const passwordMode = ref(false)

/**
 * THE SETTLING WINDOW (job #373, Tom 2026-10-02, English for Tamil speakers).
 *
 * Tom bought a course as a brand-new Tamil-interface user. Payment worked and
 * the Tamil "your plan is on" screen appeared — but in between, while the
 * emailed code, the session and the entitlement were still settling, the
 * GENERIC English "Sign in or create account" modal popped up with a spinner on
 * its button. The buyer had just signed in; the app was merely not finished
 * knowing it.
 *
 * So while something holds the door shut — the buyer's code-to-checkout hand-off,
 * or a paid-for purchase that has not landed — the generic modal neither opens
 * nor stays open. A request to open it during the window is DROPPED, not queued:
 * a modal that waits and appears the moment the window ends is the same flash
 * one second later. Holds are named so two independent windows cannot release
 * each other's.
 */
const holds = ref<string[]>([])

const suppressed = computed(() => holds.value.length > 0)
// What the modal actually renders from: asked for AND not held shut.
const isOpen = computed(() => wantsOpen.value && !suppressed.value)

const open = (opts?: { inviteCode?: boolean; password?: boolean }) => {
  if (suppressed.value) return
  inviteCodeMode.value = opts?.inviteCode ?? false
  // EMAIL FIRST (Aran, 2026-10-16), which supersedes job #129's password-first
  // default: the box now asks the server whether the address has a password
  // and shows the password box to exactly those who do. `password: true` is a
  // caller saying "this person signs in by password" (the redeem page's dead
  // end), and the box then goes straight to the password after Continue.
  passwordMode.value = opts?.password === true
  wantsOpen.value = true
}

const close = () => {
  wantsOpen.value = false
  inviteCodeMode.value = false
  passwordMode.value = false
}

/** Shut the door for `reason`. Idempotent; also takes down a modal already open. */
export function holdAuthModal(reason: string): void {
  if (!holds.value.includes(reason)) holds.value = [...holds.value, reason]
  close()
}

/** Release `reason`'s hold. The door reopens only when no hold is left. */
export function releaseAuthModal(reason: string): void {
  if (holds.value.includes(reason)) holds.value = holds.value.filter((r) => r !== reason)
}

/**
 * Composable for managing the unified auth modal.
 * Uses module-level state for true singleton behavior.
 * All components calling this share the same refs.
 */
export function useAuthModal() {
  return {
    isOpen,
    suppressed: readonly(suppressed),
    inviteCodeMode: readonly(inviteCodeMode),
    passwordMode: readonly(passwordMode),
    open,
    close,
  }
}
